import React, { useState, useEffect } from "react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { investmentPlans } from "../../utils/plans";
import { sendTradeStartEmails } from "../../lib/TradeEmail";

const StartTrade = ({ user, balance }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansDoc = await getDoc(doc(db, "users", "plans"));
        if (plansDoc.exists()) {
          const plansData = plansDoc.data();
          const plansArray = Object.entries(plansData).map(([key, plan]) => ({
            key,
            ...plan,
          }));
          if (plansArray.length > 0) {
            setPlans(plansArray);
            return;
          }
        }
      } catch (err) {
        console.error("Error fetching plans:", err);
      }
      setPlans(investmentPlans);
    };
    fetchPlans();
  }, []);

  const selectedPlanData = plans.find((p) => p.name === selectedPlan);

  const minAmount = selectedPlanData?.amount || 0;
  const maxAmount = selectedPlanData?.maxAmount || selectedPlanData?.amount * 2 || minAmount;
  const estimatedReturn = selectedPlanData && amount
    ? (parseFloat(amount) * (selectedPlanData.profit / 100)).toFixed(2)
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedPlan || !amount) {
      setError("Please select a plan and enter an amount");
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (parsedAmount < minAmount) {
      setError(`Minimum investment is $${minAmount}`);
      return;
    }

    const userBalance = balance?.Investments || 0;
    if (parsedAmount > userBalance) {
      setError("Insufficient balance in Investments account");
      return;
    }

    setLoading(true);
    try {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + selectedPlanData.duration);

      const activeTrade = {
        name: selectedPlanData.name,
        amount: parsedAmount,
        earnings: parseFloat(estimatedReturn),
        profit: selectedPlanData.maxTps || 0,
        createDate: new Date().toISOString(),
        endDate: endDate.toISOString(),
        hardware: selectedPlanData.hardware,
        specs: selectedPlanData.specs,
        duration: selectedPlanData.duration,
        profitPercentage: selectedPlanData.profit,
      };

      const startDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      const formattedEndDate = endDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

      const transaction = {
        account: "Investments",
        amount: parsedAmount,
        beneficiary: "Trading",
        beneficiaryAccount: "Sky FXT",
        date: startDate,
        description: `Investment started - ${selectedPlanData.name}`,
        type: "debit",
      };

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      await updateDoc(userDocRef, {
        activeTrade,
        role: "investor",
        group: "activated",
        transactions: [...(userData.transactions || []), transaction],
      });

      await sendTradeStartEmails(user.email, {
        userId: user.uid,
        userName: userData.fullName || "User",
        userEmail: user.email,
        planName: selectedPlanData.name,
        amount: parsedAmount,
        duration: selectedPlanData.duration,
        date: startDate,
        endDate: formattedEndDate,
      });

      window.location.reload();
    } catch (err) {
      console.error("Error starting trade:", err);
      setError("Failed to start trade. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="start-trade-form">
      <h2 className="start-trade-title">Start Trading</h2>

      <div className="start-trade-balance">
        <span className="start-trade-balance-label">Available Balance:</span>
        <span className="start-trade-balance-value">
          ${(balance?.Investments || 0).toLocaleString()}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="start-trade-fields">
        <div className="start-trade-field">
          <label>Select Plan</label>
          <select
            value={selectedPlan}
            onChange={(e) => {
              setSelectedPlan(e.target.value);
              setAmount("");
            }}
            className="start-trade-select"
          >
            <option value="">-- Choose a Plan --</option>
            {plans.map((plan, index) => (
              <option key={index} value={plan.name}>
                {plan.name} - ${plan.amount}
              </option>
            ))}
          </select>
        </div>

        {selectedPlanData && (
          <div className="start-trade-preview">
            <h3>Plan Preview</h3>
            <div className="start-trade-preview-desc">
              <span className="preview-label">Description</span>
              <span className="preview-value">{selectedPlanData.hardware}</span>
            </div>
            <div className="start-trade-preview-stats">
              <div className="start-trade-preview-stat">
                <span className="preview-label">Duration</span>
                <span className="preview-value">{selectedPlanData.duration} {selectedPlanData.duration === 1 ? "day" : "days"}</span>
              </div>
              <div className="start-trade-preview-stat">
                <span className="preview-label">Min Amount</span>
                <span className="preview-value">${minAmount}</span>
              </div>
              <div className="start-trade-preview-stat">
                <span className="preview-label">Profit</span>
                <span className="preview-value">{selectedPlanData.profit}%</span>
              </div>
            </div>
          </div>
        )}

        <div className="start-trade-field">
          <label>Amount ($)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Min: $${minAmount}`}
            min={minAmount}
            max={maxAmount}
            className="start-trade-input"
            disabled={!selectedPlan}
          />
          {selectedPlanData && (
            <span className="start-trade-hint">
              Min: ${minAmount} | Max: ${maxAmount}
            </span>
          )}
        </div>

        {amount && selectedPlanData && (
          <div className="trading-status-number">
            <span className="estimate-label">Estimated Return: </span>
            <span className="estimate-value">${estimatedReturn}</span>
          </div>
        )}

        {error && <div className="start-trade-error">{error}</div>}

        <button
          type="submit"
          className="start-trade-submit"
          disabled={loading || !selectedPlan || !amount}
        >
          {loading ? "Processing..." : "Start Trade"}
        </button>
      </form>
    </div>
  );
};

export default StartTrade;